import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

import ContactRecord from './ContactRecord'
import CustomerDetailsCard from './CustomerDetailsCard'
import CustomerService from '@/services/CustomerService'

export default function CustomerContactRecord() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const [customerDetails, setCustomerDetails] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	useEffect(() => {
		if (!id) {
			setCustomerDetails(null)
			setError(null)
			setLoading(false)
			return
		}

		let alive = true
		setLoading(true)
		setError(null)

		;(async () => {
			try {
				const { data, error: fetchError } = await CustomerService.getCustomerById(id)
				if (!alive) return
				if (fetchError) {
					setError(fetchError)
					setCustomerDetails(null)
				} else {
					setCustomerDetails(data)
				}
			} catch (err) {
				if (!alive) return
				setError(err.message || t('customers.details.messages.loadFailed'))
				setCustomerDetails(null)
			} finally {
				if (alive) setLoading(false)
			}
		})()

		return () => {
			alive = false
		}
	}, [id, t])

	if (loading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-gray-600">{t('customers.contactRecord.loading')}</div>
			</div>
		)
	}

	if (error) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-red-600">Error: {error}</div>
			</div>
		)
	}

	if (!id) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-gray-600">{t('customers.contactRecord.noCustomerSelected')}</div>
			</div>
		)
	}

	const handleCustomerUpdate = (updatedCustomer) => {
		setCustomerDetails(updatedCustomer)
	}

	const handleCustomerNumberUpdate = (phoneNumber) => {
		setCustomerDetails({ ...customerDetails, tel: phoneNumber })
	}

	const handleSalesRepNumberUpdate = (salesRepNumber) => {
		setCustomerDetails({ ...customerDetails, salesRepNumber: salesRepNumber })
	}

	return (
			<div>
				{/* Header with back button */}
				<div className="mb-6 flex items-center gap-4">
					<button
						type="button"
						onClick={() => navigate(`/system/customers/details/${id}`)}
						className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
					>
						<ArrowLeftIcon className="h-5 w-5" />
						<span>{t('customers.contactRecord.backToDetails')}</span>
					</button>
					<h1 className="text-2xl font-semibold text-gray-900">
						{t('customers.contactRecord.title')}{customerDetails?.name ? ` - ${customerDetails.name}` : ''}
					</h1>
				</div>

				{/* Customer Details Card */}
				<div className="mb-6">
					<CustomerDetailsCard
						customerId={id}
						customerDetails={customerDetails}
						onCustomerNumberUpdate={handleCustomerNumberUpdate}
						onSalesRepNumberUpdate={handleSalesRepNumberUpdate}
					/>
				</div>

				{/* Contact Record Component */}
				<ContactRecord customerId={id} customerDetails={customerDetails} onCustomerNumberUpdate={handleCustomerNumberUpdate} onSalesRepNumberUpdate={handleSalesRepNumberUpdate} />
			</div>
		)
}
